import { z } from 'zod';

import { ListingVersion } from './listing';
import { DateStringSchema, LocalDateTimeStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { HostSchema, makeEndpoint } from '../common';
import { SessionStatus, SessionStatusFilter, SessionUnavailableReason } from '../event/sessions';
import { PriceTierSchema } from '../experience/price-tier-rules';

export const PublicGetListingSessionsQuerySchema = z.object({
  from: DateStringSchema,
  to: DateStringSchema,
  resourceGroupId: z.string().uuid().optional(),
  experienceId: z.string().uuid().optional(),
  version: z.nativeEnum(ListingVersion).optional(),
  includeEventsAfterBookingDeadline: z.literal('true').optional(),
  waitlistEntryId: z.string().uuid().optional(),
  maxSessionsPerDay: z.coerce.number().int().positive().optional(),
  // `available`: only sessions with no participants and not overlap-cancelled (the embed's
  // availability predicate), ranked ahead of dead rows inside the `maxSessionsPerDay` cap.
  status: z.nativeEnum(SessionStatusFilter).optional(),
});

export const PublicGetListingSessionsResponseSchema = z.object({
  sessions: z.array(
    z.object({
      sessionId: z.string().uuid().nullable(),
      scheduleId: z.string().uuid().nullable(),
      isRepeating: z.boolean(),
      isPrivateEvent: z.boolean(),
      priceTiers: z.array(PriceTierSchema.required({ maxQuantity: true }).extend({ participantsCount: z.number().min(0) })),
      startDateTime: LocalDateTimeStringSchema,
      duration: z.number().int().positive(),
      participantCount: z.number().int().nonnegative(),
      capacity: z.number().nullable(), // open experiences do not have a capacity
      experienceId: z.string().uuid(),
      status: z.nativeEnum(SessionStatus),
      hostedBy: HostSchema.nullable(),
      isContinuousAvailability: z.boolean().optional(),
      isBookableAvailability: z.boolean().optional(),
      unavailableReason: z.nativeEnum(SessionUnavailableReason).optional(),
    }),
  ),
});

export const PublicGetListingSessionsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId, query }: { listingId: string; query: z.infer<typeof PublicGetListingSessionsQuerySchema> }) =>
    `/v1/listings/${listingId}/sessions?${buildQueryString(query)}`,
  responseSchema: PublicGetListingSessionsResponseSchema,
  requestSchema: z.void(),
  querySchema: PublicGetListingSessionsQuerySchema,
});

export type PublicListingSession = z.infer<typeof PublicGetListingSessionsResponseSchema>['sessions'][number];
